import posthog from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';
import { Capacitor } from '@capacitor/core';
import {
  BookingAnalyticsEvents,
  ProductAnalyticsFunnels,
  ProductAnalyticsModules,
  type ProductAnalyticsFunnel,
} from './events';
import { discoveryMatchingBookingProps } from './discoveryMatchingSession';
import { resolvePostHogApiHost, resolvePostHogUiHost } from './posthogHosts';
import {
  bookingPathContext,
  bookingOriginFromPath,
  readSignupAttribution,
} from './signupAttribution';

export const POSTHOG_APP_SURFACE = 'booking';
export const POSTHOG_EMISSION_SOURCE_CLIENT = 'client';

const APP_NAME = 'Rintintin Booking';
const APP_VERSION = process.env.REACT_APP_VERSION?.trim() || '0.1.0';
const APP_ENVIRONMENT =
  process.env.REACT_APP_ENVIRONMENT?.trim() ||
  (process.env.NODE_ENV === 'production' ? 'production' : 'development');

let postHogReady = false;
let postHogInitialized = false;

const PAGE_VIEW_QUEUE_MAX = 12;
const pageViewQueue: Array<{ pathname: string; search: string }> = [];
let lastCapturedPageViewKey = '';

function pageViewKey(pathname: string, search: string): string {
  return `${pathname}${search || ''}`;
}

function enqueueBookingPageView(pathname: string, search: string): void {
  const key = pageViewKey(pathname, search);
  const last = pageViewQueue[pageViewQueue.length - 1];
  if (last && pageViewKey(last.pathname, last.search) === key) return;
  if (pageViewQueue.length >= PAGE_VIEW_QUEUE_MAX) pageViewQueue.shift();
  pageViewQueue.push({ pathname, search });
}

function captureBookingPageViewNow(pathname: string, search: string): void {
  if (typeof window === 'undefined') return;
  try {
    posthog.capture(
      BookingAnalyticsEvents.PAGE_VIEW,
      bookingPageViewPayload(pathname, search, window.location.origin)
    );
    lastCapturedPageViewKey = pageViewKey(pathname, search);
  } catch {
    /* ignore */
  }
}

export type BookingAnalyticsUser = {
  user_id?: number | null;
  id?: number | null;
  email?: string | null;
  name?: string | null;
  lastname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export function resolveBookingUserId(user: BookingAnalyticsUser | null | undefined): number | null {
  const raw = user?.user_id ?? user?.id;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function isPostHogSupportedPlatform(): boolean {
  if (typeof window === 'undefined') return false;
  // App Store 5.1.2(i) — aucun analytics cookie-based sur iOS natif (miroir Pro).
  const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  return !isNativeIOS;
}

export function isPostHogReady(): boolean {
  if (!postHogReady || !isPostHogSupportedPlatform()) return false;
  try {
    return !posthog.has_opted_out_capturing();
  } catch {
    return false;
  }
}

export function getPostHogInitOptions(): Partial<PostHogConfig> {
  return {
    api_host: resolvePostHogApiHost(),
    ui_host: resolvePostHogUiHost(),
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    enable_recording_console_log: false,
    persistence: 'localStorage+cookie',
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-no-capture]',
    },
    loaded: () => {
      postHogReady = true;
      registerBookingSuperProperties();
    },
    debug: process.env.NODE_ENV === 'development' && process.env.REACT_APP_POSTHOG_DEBUG === 'true',
  };
}

export function initBookingPostHog(apiKey: string): void {
  if (!apiKey || !isPostHogSupportedPlatform()) return;
  if (postHogInitialized) {
    postHogReady = true;
    registerBookingSuperProperties();
    return;
  }
  postHogInitialized = true;
  posthog.init(apiKey, getPostHogInitOptions());
}

export function optInBookingPostHog(): void {
  if (!isPostHogSupportedPlatform()) return;
  try {
    posthog.opt_in_capturing();
    postHogReady = true;
    registerBookingSuperProperties();
  } catch {
    /* ignore */
  }
}

export function optOutBookingPostHog(): void {
  postHogReady = false;
  pageViewQueue.length = 0;
  try {
    posthog.opt_out_capturing();
  } catch {
    /* ignore */
  }
}

/**
 * Après init + opt-in : rejoue la file des $pageview, capture l'URL courante, démarre le replay si actif.
 * Évite le doublon avec PostHogPageViews via lastCapturedPageViewKey.
 */
export function bootstrapBookingPostHogAfterConsent(): void {
  if (!isPostHogReady()) return;

  const pending = pageViewQueue.splice(0, pageViewQueue.length);
  for (const item of pending) {
    const key = pageViewKey(item.pathname, item.search);
    if (key === lastCapturedPageViewKey) continue;
    captureBookingPageViewNow(item.pathname, item.search);
  }

  if (typeof window !== 'undefined') {
    captureBookingPageView(window.location.pathname, window.location.search || '');
  }

  try {
    const ph = posthog as typeof posthog & { startSessionRecording?: () => void };
    if (typeof ph.startSessionRecording === 'function') {
      ph.startSessionRecording();
    }
  } catch {
    /* ignore */
  }
}

export function registerBookingSuperProperties(): void {
  if (!isPostHogReady()) return;
  try {
    const entry = readSignupAttribution();
    posthog.register({
      app_surface: POSTHOG_APP_SURFACE,
      emission_source: POSTHOG_EMISSION_SOURCE_CLIENT,
      app_name: APP_NAME,
      app_version: APP_VERSION,
      environment: APP_ENVIRONMENT,
      platform: 'web',
      ...(entry
        ? {
            entry_path: entry.entry_path,
            entry_origin: bookingOriginFromPath(entry.entry_path, entry.utm_source),
            entry_route_zone: entry.route_zone,
            ...(entry.pro_slug ? { entry_pro_slug: entry.pro_slug } : {}),
            ...(entry.utm_source ? { entry_utm_source: entry.utm_source } : {}),
            ...(entry.utm_medium ? { entry_utm_medium: entry.utm_medium } : {}),
            ...(entry.utm_campaign ? { entry_utm_campaign: entry.utm_campaign } : {}),
          }
        : {}),
    });
  } catch {
    /* Analytics must never break the app. */
  }
}

export function identifyBookingUser(user: BookingAnalyticsUser | null | undefined): void {
  const userId = resolveBookingUserId(user);
  if (!userId || !isPostHogReady()) return;

  const first = (user?.name || user?.firstName || '').trim();
  const last = (user?.lastname || user?.lastName || '').trim();
  const displayName = [first, last].filter(Boolean).join(' ').trim();

  try {
    posthog.identify(`user_${userId}`, {
      user_id: userId,
      email: user?.email ?? null,
      name: displayName || first || null,
    });
    posthog.register({ user_id: userId });
  } catch {
    /* ignore */
  }
}

export function captureBookingEvent(event: string, properties?: Record<string, unknown>): void {
  if (!isPostHogReady()) return;
  try {
    posthog.capture(event, {
      app_surface: POSTHOG_APP_SURFACE,
      emission_source: POSTHOG_EMISSION_SOURCE_CLIENT,
      ...properties,
    });
  } catch {
    /* ignore */
  }
}

export function trackBoardingEvent(
  event:
    | 'boarding_slot_selected'
    | 'boarding_price_simulated'
    | 'boarding_request_failed'
    | 'boarding_stay_requested',
  properties?: Record<string, unknown>
): void {
  captureBookingEvent(event, {
    module: 'boarding',
    ...properties,
  });
}

export function captureBookingFunnelStep(
  event: string,
  funnel: ProductAnalyticsFunnel,
  step: string,
  properties?: Record<string, unknown>
): void {
  captureBookingEvent(event, {
    funnel,
    step,
    ...properties,
  });
}

function resolveBookingMarketingSurface(
  pathname: string
): 'blog' | 'services' | 'embed' | 'other_public' {
  if (pathname.startsWith('/embed')) return 'embed';
  if (pathname === '/blog' || pathname.startsWith('/blog/')) return 'blog';
  if (pathname.startsWith('/services/') || pathname.includes('/services/')) return 'services';
  return 'other_public';
}

const BOOKING_CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

/** UTM de l’URL courante, pour les propriétés $pageview (bouton Réserver Meta, etc.). */
export function readBookingCampaignParams(search: string): Record<string, string> {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const out: Record<string, string> = {};
  for (const key of BOOKING_CAMPAIGN_KEYS) {
    const value = params.get(key)?.trim();
    if (value) out[key] = value;
  }
  return out;
}

export function bookingPageViewPayload(
  pathname: string,
  search: string,
  origin: string
) {
  const path = `${pathname}${search || ''}`;
  return {
    app_surface: POSTHOG_APP_SURFACE,
    emission_source: POSTHOG_EMISSION_SOURCE_CLIENT,
    pathname,
    route_zone: pathname.startsWith('/embed') ? 'embed' : 'public',
    marketing_surface: resolveBookingMarketingSurface(pathname),
    search: search || undefined,
    $current_url: `${origin}${path}`,
    ...readBookingCampaignParams(search),
    ...bookingPathContext(
      pathname,
      new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('utm_source')
    ),
  };
}

export function captureBookingPageView(pathname: string, search: string): void {
  if (typeof window === 'undefined') return;
  if (!isPostHogReady()) {
    enqueueBookingPageView(pathname, search);
    return;
  }

  const key = pageViewKey(pathname, search);
  if (key === lastCapturedPageViewKey) return;
  captureBookingPageViewNow(pathname, search);
}

export function trackBookingLogin(user: BookingAnalyticsUser): void {
  const userId = resolveBookingUserId(user);
  captureBookingFunnelStep(
    BookingAnalyticsEvents.USER_LOGGED_IN,
    ProductAnalyticsFunnels.PRO_ACTIVATION,
    'logged_in',
    {
      module: ProductAnalyticsModules.SIGNUP,
      user_id: userId,
    }
  );
  identifyBookingUser(user);
}

export function trackBookingLogout(): void {
  captureBookingEvent(BookingAnalyticsEvents.USER_LOGGED_OUT);
  resetBookingPostHog();
}

export function trackBookingSignupStarted(): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.SIGNUP_STARTED,
    ProductAnalyticsFunnels.PRO_ACTIVATION,
    'signup_started',
    { module: ProductAnalyticsModules.SIGNUP }
  );
}

export function trackBookingSignupCompleted(user: BookingAnalyticsUser): void {
  const userId = resolveBookingUserId(user);
  captureBookingFunnelStep(
    BookingAnalyticsEvents.SIGNUP_COMPLETED,
    ProductAnalyticsFunnels.PRO_ACTIVATION,
    'signup_completed',
    {
      module: ProductAnalyticsModules.SIGNUP,
      user_id: userId,
    }
  );
  identifyBookingUser(user);
}

/** Surface de création réservation — séparable dans HogQL / Rantanplan. */
export type BookingCreationFlow = 'cart' | 'legacy_book' | 'backoffice';

export type BookingCreationPaymentMode = 'stripe' | 'onsite' | 'credits' | 'forfait' | 'classic';

export function trackBookingServiceSelected(props: {
  pro_id: number;
  service_id: number;
}): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.BOOKING_SERVICE_SELECTED,
    ProductAnalyticsFunnels.BOOKING_LOOP,
    'service_selected',
    { module: ProductAnalyticsModules.RESERVATIONS, flow: 'cart', ...props }
  );
}

export function trackBookingSlotSelected(props: {
  pro_id: number;
  service_id?: number | null;
  kind: 'time' | 'session';
}): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.BOOKING_SLOT_SELECTED,
    ProductAnalyticsFunnels.BOOKING_LOOP,
    'slot_selected',
    { module: ProductAnalyticsModules.RESERVATIONS, flow: 'cart', ...props }
  );
}

export function trackBookingAddedToCart(props: {
  pro_id: number;
  service_id: number;
}): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.BOOKING_ADDED_TO_CART,
    ProductAnalyticsFunnels.BOOKING_LOOP,
    'added_to_cart',
    { module: ProductAnalyticsModules.RESERVATIONS, flow: 'cart', ...props }
  );
}

export function trackBookingCreationStarted(props: {
  pro_id: number;
  service_id?: number | null;
  user_id?: number | null;
  flow?: BookingCreationFlow;
}): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.BOOKING_CREATION_STARTED,
    ProductAnalyticsFunnels.BOOKING_LOOP,
    'creation_started',
    {
      module: ProductAnalyticsModules.RESERVATIONS,
      source: 'client',
      ...discoveryMatchingBookingProps(props.pro_id),
      ...props,
    }
  );
}

export function trackBookingCreationCompleted(props: {
  pro_id: number;
  service_id?: number | null;
  booking_id: number;
  user_id?: number | null;
  flow?: BookingCreationFlow;
  payment_mode?: BookingCreationPaymentMode;
}): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.BOOKING_CREATION_COMPLETED,
    ProductAnalyticsFunnels.BOOKING_LOOP,
    'creation_completed',
    {
      module: ProductAnalyticsModules.RESERVATIONS,
      source: 'client',
      ...discoveryMatchingBookingProps(props.pro_id),
      ...props,
    }
  );
}

export function trackProEmailContactSent(props: {
  pro_id: number;
  pro_slug?: string;
  service_id?: number;
  contact_reason?: string;
  user_id?: number | null;
  is_authenticated?: boolean;
}): void {
  captureBookingFunnelStep(
    BookingAnalyticsEvents.PRO_EMAIL_CONTACT_SENT,
    ProductAnalyticsFunnels.BOOKING_LOOP,
    'pro_email_contact_sent',
    {
      module: ProductAnalyticsModules.RESERVATIONS,
      ...discoveryMatchingBookingProps(props.pro_id),
      ...props,
    }
  );
}

export function trackSearchResultsShown(props: {
  search_id: string;
  result_count: number;
  top5_pro_ids: number[];
  zero_results: boolean;
  mode: 'search' | 'ask';
  sort?: string | null;
  profession?: string | null;
}): void {
  captureBookingEvent(BookingAnalyticsEvents.SEARCH_RESULTS_SHOWN, {
    funnel: ProductAnalyticsFunnels.BOOKING_LOOP,
    module: ProductAnalyticsModules.RESERVATIONS,
    ...props,
    top5_pro_ids_csv: props.top5_pro_ids.join(','),
  });
}

export function trackDiscoveryProClicked(props: {
  search_id: string | null;
  pro_id: number;
  rank_position: number;
  ranking_score?: number | null;
  mode?: 'search' | 'ask';
  discovery_match_top5?: boolean;
}): void {
  captureBookingEvent(BookingAnalyticsEvents.DISCOVERY_PRO_CLICKED, {
    funnel: ProductAnalyticsFunnels.BOOKING_LOOP,
    module: ProductAnalyticsModules.RESERVATIONS,
    ...props,
  });
}

export type BlogArticleSharePlatform = 'twitter' | 'linkedin' | 'facebook' | 'copy' | 'native';
export type BlogArticleCtaType = 'signup' | 'booking' | 'pricing' | 'other';
export type BlogArticleScrollDepth = 25 | 50 | 75 | 100;

type BlogArticleBaseProps = {
  slug: string;
  categorySlug?: string | null;
  locale?: string | null;
};

/** Vue dédiée d'un article blog (site booking). */
export function trackBlogArticleView(props: BlogArticleBaseProps): void {
  captureBookingEvent(BookingAnalyticsEvents.BLOG_ARTICLE_VIEW, {
    slug: props.slug,
    site: 'booking',
    category_slug: props.categorySlug ?? null,
    locale: props.locale ?? null,
  });
}

/** Seuil de scroll atteint sur un article blog (site booking). */
export function trackBlogArticleScroll(
  props: BlogArticleBaseProps & { depth: BlogArticleScrollDepth }
): void {
  captureBookingEvent(BookingAnalyticsEvents.BLOG_ARTICLE_SCROLL, {
    slug: props.slug,
    site: 'booking',
    depth: props.depth,
    category_slug: props.categorySlug ?? null,
    locale: props.locale ?? null,
  });
}

/** Clic CTA depuis un article blog (site booking). */
export function trackBlogArticleCtaClicked(
  props: BlogArticleBaseProps & {
    ctaType: BlogArticleCtaType;
    destination?: string | null;
  }
): void {
  captureBookingEvent(BookingAnalyticsEvents.BLOG_ARTICLE_CTA_CLICKED, {
    slug: props.slug,
    site: 'booking',
    cta_type: props.ctaType,
    category_slug: props.categorySlug ?? null,
    destination: props.destination ?? null,
    locale: props.locale ?? null,
  });
}

/** Clic article lié depuis une page article (site booking). */
export function trackBlogArticleRelatedClicked(
  props: BlogArticleBaseProps & { targetSlug: string }
): void {
  captureBookingEvent(BookingAnalyticsEvents.BLOG_ARTICLE_RELATED_CLICKED, {
    slug: props.slug,
    site: 'booking',
    target_slug: props.targetSlug,
    category_slug: props.categorySlug ?? null,
    locale: props.locale ?? null,
  });
}

/** Partage d'un article blog (site booking). */
export function trackBlogArticleShare(props: {
  platform: BlogArticleSharePlatform;
  slug: string;
  locale?: string | null;
}): void {
  captureBookingEvent(BookingAnalyticsEvents.BLOG_ARTICLE_SHARE, {
    platform: props.platform,
    slug: props.slug,
    site: 'booking',
    locale: props.locale ?? null,
  });
}

export function trackCarnetEvent(
  event:
    | typeof BookingAnalyticsEvents.CARNET_VIEWED
    | typeof BookingAnalyticsEvents.CARNET_MARK_DONE
    | typeof BookingAnalyticsEvents.CARNET_SHARE_CREATED
    | typeof BookingAnalyticsEvents.CARNET_SCAN_IMPORTED
    | typeof BookingAnalyticsEvents.CARNET_NIGHT_ASK_SAVED
    | typeof BookingAnalyticsEvents.CARNET_BLOCK_ASK_SAVED
    | typeof BookingAnalyticsEvents.CARNET_BRIEF_ASK_SAVED,
  properties?: Record<string, unknown>
): void {
  captureBookingEvent(event, {
    module: 'carnet',
    ...properties,
  });
}

export function resetBookingPostHog(): void {
  postHogReady = false;
  postHogInitialized = false;
  try {
    posthog.reset();
  } catch {
    /* ignore */
  }
}
